import React, { useEffect, useMemo, useState } from 'react';
import DENTModalOverviewSubButtonsSelection from '../../DENTModalOverviewSubButtonsSelection/DENTModalOverviewSubButtonsSelection';
import useTooth from '../../../Hooks/Tooth/useTooth';
import {
	ToothRestorationDetail,
	ToothRestorationFillingValues,
	ToothRestorationMaterial,
	ToothRestorationQuality,
} from '../../../../../graphql/graphql';
import DENTMultipleSelectList from '../../DENTMultipleSelectList/DENTMultipleSelectList';
import { useDENTContext } from '../../../Services/DENTContext';
import useNotification from '../../../../../Hooks/useNotification.hook';
import { RESTORATION_TRANSLATION_MAP } from '../../../Helpers/restoration/restorationTranslationMap.helper';
import DENTModalOverviewRestorationFillingListItem from './DENTModalOverviewRestorationFillingListItem';

interface Props {
	fillingIndex: number;
	setFillingIndex: (index: number) => void;
}

const DENTModalOverviewRestorationFilling = ({
	fillingIndex,
	setFillingIndex,
}: Props) => {
	const {
		restoration: { fillings },
	} = useTooth();
	const { activeTooth } = useDENTContext();
	const { showNotificationAlert } = useNotification();

	const [values, setValues] = useState<ToothRestorationFillingValues>(
		fillings.getValues(fillingIndex),
	);

	useEffect(() => {
		if (values) {
			fillings.setValues(fillingIndex, values);
		}
	}, [values]);

	useEffect(() => {
		const newValues = fillings.getValues(fillingIndex) ?? {};
		fillings.setValues(fillingIndex, newValues);
		setValues({ ...newValues });
	}, [fillingIndex, setFillingIndex]);

	const handleAddFilling = () => {
		if (activeTooth?.restoration?.fillings?.[fillingIndex]?.surfaces?.length) {
			setFillingIndex(fillingIndex + 1);
		} else {
			showNotificationAlert({
				content:
					'patientTreatment.meddiDENT.dentalCross.pathologyModal.decay.errorNoValuesSelected',
			});
		}
	};

	const canAddNewFilling = useMemo(() => {
		const activeFilling = activeTooth?.restoration?.fillings?.[fillingIndex];
		return Boolean(activeFilling?.surfaces?.length);
	}, [activeTooth?.restoration?.fillings?.[fillingIndex]]);

	const handleChangeMaterial = (value: ToothRestorationMaterial) => {
		setValues(prev => {
			return prev?.material === value
				? {
						material: undefined,
						quality: undefined,
						detail: undefined,
				  }
				: { ...prev, material: value };
		});
	};

	const handleChangeQuality = (value: ToothRestorationQuality) => {
		setValues(prev => {
			return prev?.quality === value
				? {
						quality: undefined,
						detail: undefined,
				  }
				: { ...prev, quality: value };
		});
	};

	const handleChangeDetail = (value: ToothRestorationDetail) => {
		setValues(prev => ({
			...prev,
			detail: prev?.detail === value ? undefined : value,
		}));
	};

	const rows = [
		{
			shouldHide: !fillings.getSurfaces(fillingIndex).length,
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.material.title',
			items: [
				{
					title:
						RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.Composite],
					isSelected: values?.material === ToothRestorationMaterial.Composite,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.Composite),
				},
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.Ceramic],
					isSelected: values?.material === ToothRestorationMaterial.Ceramic,
					onClick: () => handleChangeMaterial(ToothRestorationMaterial.Ceramic),
				},
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.Amalgam],
					isSelected: values?.material === ToothRestorationMaterial.Amalgam,
					onClick: () => handleChangeMaterial(ToothRestorationMaterial.Amalgam),
				},
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.Gold],
					isSelected: values?.material === ToothRestorationMaterial.Gold,
					onClick: () => handleChangeMaterial(ToothRestorationMaterial.Gold),
				},
				{
					title:
						RESTORATION_TRANSLATION_MAP[
							ToothRestorationMaterial.NonPreciousMaterial
						],
					isSelected:
						values?.material === ToothRestorationMaterial.NonPreciousMaterial,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.NonPreciousMaterial),
				},
				{
					title:
						RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.Temporary],
					isSelected: values?.material === ToothRestorationMaterial.Temporary,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.Temporary),
				},
				{
					title:
						RESTORATION_TRANSLATION_MAP[ToothRestorationMaterial.GlassIonomer],
					isSelected:
						values?.material === ToothRestorationMaterial.GlassIonomer,
					onClick: () =>
						handleChangeMaterial(ToothRestorationMaterial.GlassIonomer),
				},
			],
		},
		{
			shouldHide: !values?.material,
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.quality.title',
			items: [
				{
					title:
						RESTORATION_TRANSLATION_MAP[ToothRestorationQuality.Sufficient],
					isSelected: values?.quality === ToothRestorationQuality.Sufficient,
					onClick: () =>
						handleChangeQuality(ToothRestorationQuality.Sufficient),
				},
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationQuality.Uncertain],
					isSelected: values?.quality === ToothRestorationQuality.Uncertain,
					onClick: () => handleChangeQuality(ToothRestorationQuality.Uncertain),
				},
				{
					title:
						RESTORATION_TRANSLATION_MAP[ToothRestorationQuality.Insufficient],
					isSelected: values?.quality === ToothRestorationQuality.Insufficient,
					onClick: () =>
						handleChangeQuality(ToothRestorationQuality.Insufficient),
				},
			],
		},
		{
			shouldHide: !(values?.material && values?.quality),
			title:
				'patientTreatment.meddiDENT.dentalCross.restorationModal.detail.title',
			items: [
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationDetail.Overhang],
					isSelected: values?.detail === ToothRestorationDetail.Overhang,
					onClick: () => handleChangeDetail(ToothRestorationDetail.Overhang),
				},
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationDetail.Flush],
					isSelected: values?.detail === ToothRestorationDetail.Flush,
					onClick: () => handleChangeDetail(ToothRestorationDetail.Flush),
				},
				{
					title: RESTORATION_TRANSLATION_MAP[ToothRestorationDetail.Shortfall],
					isSelected: values?.detail === ToothRestorationDetail.Shortfall,
					onClick: () => handleChangeDetail(ToothRestorationDetail.Shortfall),
				},
			],
		},
	];

	return (
		<div>
			<DENTModalOverviewSubButtonsSelection rows={rows} />
			<DENTMultipleSelectList
				items={activeTooth?.restoration?.fillings || []}
				addNewItem={handleAddFilling}
				showAddButton={canAddNewFilling}
				renderComponent={(item, index) => (
					<DENTModalOverviewRestorationFillingListItem
						key={index}
						filling={item}
						handleEdit={() => setFillingIndex(index)}
						handleDelete={() => fillings.reset(index)}
					/>
				)}
			/>
		</div>
	);
};

export default DENTModalOverviewRestorationFilling;
